import { FormControl, ValidationErrors } from '@angular/forms';
import moment from 'moment';
import { DATE_FORMAT } from '../constants/date.constants';
import { TranslateService } from '@ngx-translate/core';
import { API_PARAMS } from '../constants/api.constants';

/**
 * 由身分證字號判斷性別。
 * 台灣身分證第 2 碼：1 = 男、2 = 女；
 * 新式外來人口統一證號第 2 碼：8 = 男、9 = 女。
 * @param idNo 身分證/居留證號
 * @returns API_PARAMS.GENDER 的 'M'（男）/ 'F'（女）；無法判斷則回傳 ''
 */
export function getGenderFromIdNo(idNo: string | null | undefined): string {
  if (!idNo || idNo.length < 2) return '';
  const secondChar = idNo.charAt(1);
  if (secondChar === '1' || secondChar === '8') {
    return API_PARAMS.GENDER.MALE; // 'M'
  }
  if (secondChar === '2' || secondChar === '9') {
    return API_PARAMS.GENDER.FEMALE; // 'F'
  }
  return '';
}

// 生日驗證器
export function birthdayValidator(
  control: FormControl,
  translateService: TranslateService,
  ageRange?: { max: number; min: number },
  calculateAgeFn?: (birthday: string | moment.Moment) => number
): ValidationErrors | null {
  const value = control.value;
   // 格式驗證：處理 moment.Moment 或 string
  let dateStr = value;
  if (moment.isMoment(value)) {
    dateStr = (value as moment.Moment).format(DATE_FORMAT);
  }
  if (!moment(dateStr, DATE_FORMAT, true).isValid()) {
    return {
      invalidFormat: translateService.instant('error.birthday-error.format'),
    };
  }
  if (ageRange) {
    // 使用傳入的 calculateAgeFn，或 fallback 到粗略計算
    const age = calculateAgeFn ? calculateAgeFn(value) : (moment().year() - moment(dateStr, DATE_FORMAT).year());
    
    if (age < ageRange.min || age > ageRange.max) {
      return {
        invalidAge: translateService.instant(
          'error.birthday-error.age-invalid'
        ),
      };
    }
  }
  return null;
}
// 精確計算年齡（與您的 get age() 邏輯相同）
export function calculateAge(birthday: string | moment.Moment): number {
  if (!birthday) return 0;
  
  const today = moment();
  const birthDate = moment.isMoment(birthday) ? birthday : moment(birthday, DATE_FORMAT);
  
  if (!birthDate.isValid()) return 0;
  
  let age = today.year() - birthDate.year();
  if (
    today.month() < birthDate.month() ||
    (today.month() === birthDate.month() && today.date() < birthDate.date())
  ) {
    age--;
  }
  return age;
}

// 信箱驗證器
export function emailValidator(
  control: FormControl,
  translateService: TranslateService
): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return {
      invalidFormat: translateService.instant('error.email-error.format'),
    };
  }
  return null;
}

// 姓名驗證器
export function nameValidator(
  control: FormControl,
  translateService: TranslateService,
  nameMaxLength: number
): ValidationErrors | null {
  const value = control.value || '';
  // 只允許英文、中文、空格
  const regex = /^[A-Za-z\u4e00-\u9fa5\s]+$/;
  if (!regex.test(value)) {
    return {
      invalidCharacters: translateService.instant('error.format', {
        fieldName: translateService.instant('id-login.name'),
      }),
    };
  }
  // 驗證長度
  if (value.length > nameMaxLength) {
    return {
      maxLengthExceeded: translateService.instant('error.max-length', {
        fieldName: translateService.instant('id-login.name'),
        maxLength: nameMaxLength,
      }),
    };
  }
  return null;
}

// 身分證號驗證器
export function idNoValidator(
  control: FormControl,
  translateService: TranslateService
): ValidationErrors | null {
  const v = control.value || '';

  // 檢查是否包含中文字符
  if (/[\u4e00-\u9fa5]/.test(v)) {
    return {
      invalidCharacters: translateService.instant(
        'error.id-number-error.format'
      ),
    };
  }

  // 格式檢查：英文 1 碼 + 1/2 + 8 位數字
  if (!/^[A-Z][12]\d{8}$/.test(v)) {
    return {
      invalidCharacters: translateService.instant(
        'error.id-number-error.format'
      ),
    };
  }

  // A~Z 對照碼表（index = 字母序）
  const pair = [
    10, 11, 12, 13, 14, 15, 16, 17, 34, 18, 19, 20, 21, 22, 35, 23, 24, 25, 26,
    27, 28, 29, 32, 30, 31, 33,
  ];

  // 先把第一碼轉成兩位數：十位 + 個位×9
  const code = pair[v.charCodeAt(0) - 65]; // 65 = 'A'
  let sum = Math.floor(code / 10) + (code % 10) * 9;

  // 計算第 2 ~ 9 碼，權重依序 8‥1
  for (let i = 1; i < 9; i++) {
    sum += (v.charCodeAt(i) - 48) * (9 - i); // 48 = '0'
  }

  // 比對檢查碼：最後一碼 + sum 必須能被 10 整除
  const checkDigit = v.charCodeAt(9) - 48;
  const calcDigit = (10 - (sum % 10)) % 10;
  if (calcDigit !== checkDigit) {
    return {
      invalidCharacters: translateService.instant(
        'error.id-number-error.format'
      ),
    };
  }

  return null;
}

// 企業代碼驗證器
export function companyNoValidator(
  control: FormControl,
  translateService: TranslateService
): ValidationErrors | null {
  const value = control.value || '';
  // 驗證最多10字元、接受英文、數字及-
  const regex = /^[A-Za-z0-9-]{1,10}$/;
  if (!regex.test(value)) {
    return {
      invalidFormat: translateService.instant(
        'error.company-number-error.format'
      ),
    };
  }
  return null;
}

// 員工編號驗證器
export function empIdValidator(
  control: FormControl,
  translateService: TranslateService
): ValidationErrors | null {
  const value = control.value || '';
  const regex = /^[A-Za-z0-9-]{1,20}$/;
  if (!regex.test(value)) {
    return {
      invalidFormat: translateService.instant('error.employee-id-error.format'),
    };
  }
  return null;
}

// 密碼驗證器
export function passwordValidator(
  control: FormControl,
  translateService: TranslateService,
  checkLength: boolean
): ValidationErrors | null {
  const value = control.value || '';

  // 驗證8-20位英文數字組合
  const lengthValid = /^[A-Za-z0-9]{8,20}$/.test(value);

  // 驗證必須包含大小寫字母與數字
  const upperValid = /[A-Z]/.test(value);
  const lowerValid = /[a-z]/.test(value);
  const numberValid = /[0-9]/.test(value);

  const formatValid = upperValid && lowerValid && numberValid;

  // 驗證不可包含特殊符號
  const hasSpecialChar = /[^A-Za-z0-9]/.test(value);

  if ((!lengthValid && checkLength) || !formatValid || hasSpecialChar) {
    return {
      ...(lengthValid || !checkLength
        ? null
        : {
            length: translateService.instant('error.set-password-error.length'),
          }),
      ...(formatValid
        ? null
        : {
            format: translateService.instant('error.set-password-error.format'),
          }),
      ...(hasSpecialChar
        ? {
            specialChar: translateService.instant('set-password.password-hint3'),
          }
        : null),
    };
  }

  return null;
}
